/**
 * Foutmeldingen naar Digital Guardian.
 *
 * Contract: docs/GUARDIAN-INTEGRATIE.md. Waarom dit er is: docs/monitoring.md.
 * Korte versie: een verkoper zag wekenlang een lege klantenlijst doordat een
 * geweigerde Firestore-read werd opgeslokt tot een lege array. Niemand kreeg
 * daar iets van mee.
 *
 * DE GOUDEN REGEL: alles wat per voorval verschilt hoort in `context`, nooit in
 * `title`. Guardian groepeert op de titeltekst, dus een ID of pagina in de titel
 * maakt van elk voorval een eigen issue en loopt het kanaal vol. De pagina hoort
 * in `context.url` -- daar telt Guardian unieke waarden van ("13 unieke pagina's").
 */
const ENDPOINT = "https://guardian.digitalkiss.nl/api/events";

/**
 * Eén vaste slug voor deze applicatie. Het CMS is een eigen applicatie met een
 * eigen repo, deploy en domein; de webshop meldt onder zijn eigen slug. Het
 * onderscheid frontend/backend maak je met `type`, niet met `project`.
 */
const PROJECT = "bombyxx-cms";

const MAX_PER_PAGELOAD = 10;
const HERHAAL_COOLDOWN_MS = 10000;
const WACHT_OP_CONTEXT_MS = 3000;
const MAX_IN_WACHTRIJ = 10;

/**
 * Lokale ontwikkeling meldt niets. Bewust op hostname en niet op een
 * env-variabele: `.env.local` wijst naar de productie-Firebase, dus een check op
 * REACT_APP_PROJECTID zou lokaal geklungel als productiefouten melden.
 */
const isLokaal = () => {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".localhost");
};

const ENVIRONMENT = process.env.REACT_APP_PROJECTID === "bombyxx-96e9a" ? "production" : "staging";
const RELEASE = process.env.REACT_APP_RELEASE || "unknown";

/** Fouten waar je toch niets mee kunt: extensies, meetruis, cross-origin. */
const NEGEER = [
  /^Script error\.?$/,
  /ResizeObserver loop/,
  /chrome-extension:\/\//,
  /moz-extension:\/\//,
  /safari-extension:\/\//,
];

export const moetGenegeerdWorden = (tekst) => !!tekst && NEGEER.some((r) => r.test(tekst));

/**
 * Haalt ID's uit een pad zodat de titel per soort fout gelijk blijft. Zonder dit
 * wordt elke order een eigen issue en loopt het kanaal vol -- zie de gouden regel.
 */
export const normaliseerPad = (url) => {
  try {
    const pad = new URL(url, window.location.origin).pathname;
    return pad
      .split("/")
      .map((deel) => {
        if (!deel) return deel;
        if (/^\d+$/.test(deel)) return ":id";
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(deel)) return ":id";
        if (/^[A-Za-z0-9_-]{16,}$/.test(deel)) return ":id";
        return deel;
      })
      .join("/");
  } catch (e) {
    return "onbekend";
  }
};

let verstuurdAantal = 0;
const laatsteMelding = new Map();

let gedeeldeContext = {};
let contextKlaar = false;
let wachtrij = [];
let spoelTimer = null;

const verstuur = (event) => {
  try {
    fetch(ENDPOINT, {
      method: "POST",
      keepalive: true, // overleeft het verlaten van de pagina
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        project: PROJECT,
        environment: ENVIRONMENT,
        release: RELEASE,
        severity: "error",
        type: "client_crash",
        ...event,
        context: {
          userAgent: navigator.userAgent,
          ...gedeeldeContext,
          ...event.context,
        },
      }),
    }).catch(() => {
      // Melden mag nooit zelf iets breken.
    });
  } catch (e) {
    // Idem.
  }
};

const spoelWachtrij = () => {
  if (spoelTimer) {
    clearTimeout(spoelTimer);
    spoelTimer = null;
  }
  const teVersturen = wachtrij;
  wachtrij = [];
  teVersturen.forEach(verstuur);
};

/**
 * Context die bij elke melding mee moet maar die deze module niet zelf kan
 * weten -- vooral de rol. De bug die hiertoe leidde trof alleen niet-beheerders,
 * dus een melding zonder rol had ons naar de verkeerde plek gestuurd.
 *
 * Wordt aangeroepen zodra het profiel binnen is (data/hooks/useAuth.js).
 */
export const setGuardianContext = (extra) => {
  gedeeldeContext = { ...gedeeldeContext, ...extra };
  contextKlaar = true;
  spoelWachtrij();
};

/**
 * Meldingen die vallen vóórdat de rol bekend is, wachten kort. De
 * profielsnapshot landt later dan de eerste Firestore-queries, dus juist een
 * mislukte read tijdens het laden zou anders zónder rol vertrekken. Na
 * WACHT_OP_CONTEXT_MS gaan ze alsnog weg: liever een melding met minder context
 * dan geen melding.
 */
export const report = (event = {}) => {
  try {
    if (isLokaal()) return;
    if (verstuurdAantal >= MAX_PER_PAGELOAD) return;

    const sleutel = `${event.type || "unknown"}|${event.title || ""}`;
    const nu = Date.now();
    if (laatsteMelding.has(sleutel) && nu - laatsteMelding.get(sleutel) < HERHAAL_COOLDOWN_MS) return;
    laatsteMelding.set(sleutel, nu);
    verstuurdAantal += 1;

    // De pagina vastleggen op het moment van de fout, niet bij het versturen:
    // door een redirect kan dat verschillen.
    const metPagina = { ...event, context: { url: window.location.pathname, ...event.context } };

    if (!contextKlaar) {
      if (wachtrij.length < MAX_IN_WACHTRIJ) wachtrij.push(metPagina);
      if (!spoelTimer) spoelTimer = setTimeout(spoelWachtrij, WACHT_OP_CONTEXT_MS);
      return;
    }
    verstuur(metPagina);
  } catch (e) {
    // Een fout in de meldcode mag de app niet meeslepen.
  }
};

if (typeof window !== "undefined") {
  // Niets in de wachtrij laten staan als de gebruiker wegklikt.
  window.addEventListener("pagehide", spoelWachtrij);

  window.addEventListener("error", (e) => {
    const message = e.message || "";
    if (moetGenegeerdWorden(message) || moetGenegeerdWorden(e.filename || "")) return;

    // Oude bundle na een deploy: één keer herladen lost het op voor de gebruiker.
    if (/Loading chunk \d+ failed|dynamically imported module/.test(message)) {
      try {
        if (!sessionStorage.getItem("guardian:reloaded")) {
          sessionStorage.setItem("guardian:reloaded", "1");
          window.location.reload();
          return;
        }
      } catch (err) {
        // Storage geblokkeerd: dan gewoon melden.
      }
      report({ severity: "info", type: "stale_bundle", title: "Chunk laden mislukt na reload" });
      return;
    }

    report({ title: message, context: { stack: e.error && e.error.stack ? e.error.stack.slice(0, 2000) : undefined } });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e.reason && e.reason.message) || String(e.reason || "");
    if (moetGenegeerdWorden(reason)) return;
    report({
      type: "unhandled_rejection",
      title: reason,
      context: { stack: e.reason && e.reason.stack ? e.reason.stack.slice(0, 2000) : undefined },
    });
  });
}

export default report;
